import { DialogM } from "@common/components/alert/dialog/Dialog"
import { BadgeM } from "@common/components/badge/BadgeM"
import IconButtonM from "@common/components/button/IconButtonM"
import TextM from "@common/components/text/TextM"
import TitleM from "@common/components/text/TitleM"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { PaginatedFlatList } from "@common/screens/PaginatedFlatList"
import { theme } from "@common/theme/theme"
import { DimensionsContext, IS_WEB } from "@common/utils/mobileUtils"
import {
    groupAttributionProposalStatusColors,
    groupAttributionProposalStatusNames,
    substitutionProposalStatusColors,
    substitutionProposalStatusNames,
} from "@data/constants/formConstants"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { groupAttributionProposalsService } from "@data/services/groupAttributionProposalsService"
import { groupAttributionService } from "@data/services/groupAttributionService"
import { substitutionProposalsService } from "@data/services/substitutionProposalsService"
import { buildPerSessionDisplayStrings, capitalize, formatMinutes } from "@data/utility/Utils"
import { availabilityNudgeFromState } from "../availability/maDispoSlot"
import {
    acceptButtonLabel,
    buildAcceptBody,
    buildDeclineBody,
    canAccept,
    cardInfoLines,
    cardTitle,
    dialogIntro,
    dialogNote,
    dialogOptions,
    dialogTitle,
    DECLINE_BUTTON_LABEL,
    DECLINE_BACK_LABEL,
    DECLINE_CONFIRM_TITLE,
    declineConsequence,
    initialSelection,
    isSingleChoice,
    isSlotChangeProposal,
    slotChangeOf,
    toggleSlot,
} from "./slotChangeProposal"
import { useFocusEffect, useNavigation } from "@react-navigation/native"
import moment from "moment"
import React, { useContext, useEffect, useMemo, useState } from "react"
import { ActivityIndicator, TouchableOpacity, View } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { Tabs, TabScreen, TabsProvider } from "react-native-paper-tabs"
import { connect } from "react-redux"

// Contenu de la modale « Changement de créneau » : les créneaux à choisir (radio quand un
// seul est demandé, cases à cocher sinon), ceux qu'un autre tuteur a déjà pris, et ce que le
// tuteur doit savoir avant d'accepter.
const SlotChangeDialogContent = ({ offer, selected, onToggle }) => {
    const singleChoice = isSingleChoice(offer)

    return (
        <View style={{ alignSelf: "stretch" }}>
            <TextM fontSize={13} wrap center style={{ marginBottom: 12 }}>
                {dialogIntro(offer)}
            </TextM>
            {dialogOptions(offer).map((option) => {
                const isSelected =
                    !option.taken && selected.includes(option.liveSessionId)
                return (
                    <TouchableOpacity
                        key={option.key}
                        disabled={option.taken}
                        onPress={() => onToggle(option.liveSessionId)}
                        style={{
                            flexDirection: "row",
                            alignItems: "center",
                            // La bordure s'épaissit d'un point à la sélection : la marge
                            // intérieure le rend, pour que la ligne ne bouge pas.
                            paddingVertical: isSelected ? 9 : 10,
                            paddingHorizontal: isSelected ? 11 : 12,
                            marginBottom: 8,
                            borderRadius: 10,
                            borderWidth: isSelected ? 2 : 1,
                            borderColor: isSelected
                                ? theme.colors.success
                                : theme.colors.lightMore,
                            backgroundColor: theme.colors.white,
                            opacity: option.taken ? 0.5 : 1,
                        }}
                    >
                        <View
                            style={{
                                width: 16,
                                height: 16,
                                marginRight: 10,
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: singleChoice ? 8 : 4,
                                borderWidth: isSelected && singleChoice ? 5 : 2,
                                borderColor: isSelected
                                    ? theme.colors.success
                                    : theme.colors.light,
                                backgroundColor:
                                    isSelected && !singleChoice
                                        ? theme.colors.success
                                        : theme.colors.white,
                            }}
                        >
                            {isSelected && !singleChoice && (
                                <FontAwesome5
                                    name="check"
                                    size={8}
                                    color={theme.colors.white}
                                />
                            )}
                        </View>
                        <View style={{ flex: 1 }}>
                            <TextM
                                fontWeight="SemiBold"
                                color={theme.colors.blackLight}
                                style={
                                    option.taken
                                        ? { textDecorationLine: "line-through" }
                                        : undefined
                                }
                            >
                                {option.label}
                            </TextM>
                            {option.note ? (
                                <TextM fontSize={11} color={theme.colors.greyAlt} wrap>
                                    {option.note}
                                </TextM>
                            ) : null}
                        </View>
                    </TouchableOpacity>
                )
            })}
            <View
                style={{
                    marginTop: 4,
                    paddingVertical: 7,
                    paddingHorizontal: 9,
                    borderRadius: 8,
                    backgroundColor: theme.colors.primaryLighter,
                }}
            >
                <TextM fontSize={11} color={theme.colors.greyAlt} wrap>
                    {dialogNote(offer)}
                </TextM>
            </View>
            <TextM
                fontSize={12}
                color={theme.colors.error}
                wrap
                center
                style={{ marginTop: 10 }}
            >
                {declineConsequence(offer)}
            </TextM>
        </View>
    )
}


const ProposalsTabScreen = (props) => {
    const dimensions = useContext(DimensionsContext)
    const navigation = useNavigation()


    const [activeTab, setActiveTab] = useState(0)
    const [loadingItems, setLoadingItems] = useState(new Set()) // Track loading items by ID

    // refreshKey increments to force PaginatedFlatList remount (triggers fresh data fetch)
    // This prevents duplicate API calls while ensuring data stays fresh
    const [refreshKey, setRefreshKey] = useState(0)

    const groupAttributionProposals = Array.isArray(
        props.groupAttributionProposals
    )
        ? props.groupAttributionProposals
        : []
    const trialAttributionProposals = Array.isArray(
        props.trialAttributionProposals
    )
        ? props.trialAttributionProposals
        : []
    const substitutionProposals = Array.isArray(props.substitutionProposals)
        ? props.substitutionProposals
        : []

    // Check user permissions for tabs visibility
    // « Groupes » : même règle que la tab bar ({@see tutorTabScreens}) — les missions
    // fixes, ou une offre ciblée reçue. Les deux surfaces lisent le même compteur, sans
    // quoi l'onglet parent s'ouvrirait sur un écran sans onglet à afficher.
    const canShowGroupsTab =
        props.user?.profile?.fixed_missions === 1 ||
        (props.groupAttributionProposalsMeta?.targeted_count || 0) > 0
    const canShowReplacementsTab = props.user?.profile.replace_missions === 1
    const canShowTrialsTab = props.user?.profile.accept_trials === 1

    // Build available tabs array based on permissions - maintain same order as rendered tabs
    const availableTabs = []
    if (canShowGroupsTab)
        availableTabs.push({ type: "group", index: availableTabs.length })
    if (canShowTrialsTab)
        availableTabs.push({ type: "trial", index: availableTabs.length })
    if (canShowReplacementsTab)
        availableTabs.push({
            type: "substitution",
            index: availableTabs.length,
        })

    // Map current activeTab to the correct tab type
    const getCurrentTabType = () => {
        if (availableTabs.length === 0) return "group" // fallback
        const currentTab = availableTabs[activeTab] || availableTabs[0]
        return currentTab.type
    }

    const currentTabType = getCurrentTabType()

    // Get current data and service based on active tab type
    const currentProposals =
        currentTabType === "substitution"
            ? substitutionProposals
            : currentTabType === "trial"
                ? trialAttributionProposals
                : groupAttributionProposals
    const currentService =
        currentTabType === "substitution"
            ? substitutionProposalsService.getSubstitutionProposals
            : groupAttributionProposalsService.getGroupAttributionProposals

    // Build URL parameters for the API request
    const requestUrlParams = useMemo(
        () => ({
            per_page: 15,
            is_trial: currentTabType === "trial" ? 1 : 0,
        }),
        [currentTabType]
    )


    const refreshAll = () => {
        props.apiRequest(groupAttributionService.getProposalsCount)
        // Increment key to trigger PaginatedFlatList remount → fresh data fetch
        setRefreshKey((prev) => prev + 1)
    }

    // Refresh data when screen comes into focus (user navigates back to this screen)
    useFocusEffect(
        React.useCallback(() => {
            refreshAll()
        }, [])
    )


    useEffect(() => {
        refreshAll()
    }, [activeTab, currentTabType])

    // Une réponse arrivée, acceptée ou refusée : la ligne cesse de tourner.
    const stopLoading = (routeParams) =>
        setLoadingItems((prev) => {
            const next = new Set(prev)
            next.delete(routeParams?.id)
            return next
        })

    useApiStatus(
        groupAttributionProposalsService.updateGroupAttributionProposalStatus,
        null,
        true,
        (data, allData, urlParams, routeParams) => {
            // Only refresh badge counts, not the full list — the reducer updates the proposal in-place
            props.apiRequest(groupAttributionService.getProposalsCount)
            stopLoading(routeParams)
        },
        // A refused answer (e.g. « Trop tard » on a Changement de créneau) shows its
        // message in the generic error dialog; the row must not keep spinning after it.
        (error, urlParams, routeParams) => stopLoading(routeParams)
    )

    useApiStatus(
        substitutionProposalsService.updateSubstitutionProposalStatus,
        null,
        true,
        (data, allData, urlParams, routeParams) => {
            props.apiRequest(groupAttributionService.getProposalsCount)
            stopLoading(routeParams)
        }
    )

    const formatTimeSlots = (liveSessions, label = "Créneaux") => {
        if (!liveSessions || !liveSessions.length) return "Aucun créneau défini"

        // Group sessions by day
        const sessionsByDay = {}
        liveSessions.forEach((session) => {
            const dayTime = moment(
                session?.day_time,
                "dddd HH:mm",
                "en"
            ).locale("fr")
            const day = capitalize(dayTime.format("dddd"))
            const time = dayTime.format("HH[h]mm")

            if (!sessionsByDay[day]) {
                sessionsByDay[day] = []
            }

            sessionsByDay[day].push(
                `${time}-${moment(dayTime)
                    .add(session.duration, "minutes")
                    .format("HH[h]mm")}`
            )
        })

        // Format into string
        return (
            label +
            " : " +
            Object.entries(sessionsByDay)
                .map(([day, times]) => `${day} ${times.join(", ")}`)
                .join(", ")
        )
    }

    const formatSubstitutionInfo = (substitution) => {
        if (!substitution) return "Informations non disponibles"

        const liveSession = substitution?.live_session
        if (!liveSession) return "Session non définie"

        const date = moment(substitution?.date)
        const formattedDate = capitalize(date.format("dddd DD MMMM [à] HH:mm"))
        const duration = formatMinutes(liveSession?.duration)
        const subjects = liveSession?.school_subjects_classes_string

        return `${formattedDate} - ${duration}\n${subjects}`
    }


    const renderTabContent = () => {
        const isGroupProposal = currentTabType !== "substitution"
        // Already sorted by the backend: targeted offers first, then created_at desc.
        // Don't re-sort here — the list is paginated, so a client-side sort would only
        // reorder the page in hand and shuffle rows as the next page arrives.
        const dataToShow = currentProposals || []

        return (
            <View
                style={{ flex: 1, backgroundColor: theme.colors.whiteAlmost }}
            >
                {/*
                    KEY PROP EXPLANATION:
                    - key={`${currentTabType}-${refreshKey}`} forces remount when either changes
                    - currentTabType change: When switching tabs (group → trial → substitution)
                    - refreshKey change: When manually refreshing (refresh button, status update)
                    - Remount triggers PaginatedFlatList to fetch fresh data from API
                */}
                <PaginatedFlatList
                    key={`${currentTabType}-${refreshKey}`}
                    data={dataToShow}
                    requestApiService={currentService}
                    requestRouteParams={{}}
                    requestData={{}}
                    requestUrlParams={requestUrlParams}
                    renderItem={(itemProps) => renderProposalItem(itemProps, currentTabType)}
                    noActivityIndicator={true}
                    contentContainerStyle={{
                        paddingBottom:
                            Math.max(
                                20,
                                Math.min(24, dimensions?.width * 0.06)
                            ) || 20,
                        paddingTop: 0,
                        paddingHorizontal: 0,
                        alignItems: "stretch",
                    }}
                    style={{
                        flex: 1,
                        backgroundColor: theme.colors.whiteAlmost,
                        width: "100%",
                        alignSelf: "stretch",
                    }}
                    showsVerticalScrollIndicator={false}
                />
            </View>
        )
    }

    const renderProposalItem = ({ item }, tabType) => {
        const isGroupProposal = tabType !== "substitution"
        const isAdhoc = item?.group_attribution?.is_adhoc
        // Une offre ciblée est une offre dont le groupe tient un élève que ce tuteur a
        // déjà eu. C'est ce que le badge dit, et c'est aussi ce qui la remonte en tête
        // de liste côté backend.
        const isTargeted = !!item?.targeted
        const isTemporaryAbsence = item?.group_attribution?.is_temporary_absence
        // « Changement de créneau » : la famille d'un élève de ce tuteur change l'horaire
        // d'un cours ; il le garde en choisissant ses nouveaux créneaux.
        const slotChange =
            isGroupProposal && isSlotChangeProposal(item)
                ? slotChangeOf(item)
                : null
        const tempAbsenceDetails =
            item?.group_attribution?.temporary_absence_details
        const existingSessions =
            item?.group_attribution?.existing_sessions || []
        const newSessions = item?.group_attribution?.live_sessions || []

        const schoolSubjectsClassesString =
            item?.group_attribution?.school_subjects_classes_string

        // Build adhoc-specific title and info
        let groupTitle =
            schoolSubjectsClassesString || "Informations non disponibles"
        let groupInfo = formatTimeSlots(newSessions)

        if (isTemporaryAbsence && tempAbsenceDetails) {
            // Temporary absence replacement - show period instead of time slots
            groupInfo = `Remplacement temporaire sur la période du ${
                tempAbsenceDetails.first_date
            } au ${tempAbsenceDetails.last_date} (${
                tempAbsenceDetails.occurrence_count
            } ${
                tempAbsenceDetails.occurrence_count > 1 ? "séances" : "séance"
            })`
        } else if (isAdhoc) {
            // Extract just class and subject from school_subjects_classes_string
            // Format from backend: "2 élèves - 5ème - Maths" -> we want "5ème - Maths"
            const fullString = schoolSubjectsClassesString || ""
            const parts = fullString.split(" - ")
            // Remove the first part (student count) and join the rest
            const classAndSubject =
                parts.length > 1 ? parts.slice(1).join(" - ") : ""

            // Build title without student names
            if (classAndSubject) {
                groupTitle = `Nouvel élève ${classAndSubject}`
            } else {
                groupTitle = `Nouvel élève`
            }

            // Build info for adhoc proposals - existing sessions first, then new sessions
            const infoParts = []
            if (existingSessions.length > 0) {
                infoParts.push(
                    formatTimeSlots(existingSessions, "📅 Créneaux existants")
                )
            }
            if (newSessions.length > 0) {
                infoParts.push(
                    formatTimeSlots(newSessions, "➕ Nouveaux créneaux")
                )
            }
            groupInfo = infoParts.join("\n")
        }

        if (slotChange) {
            groupTitle = cardTitle(slotChange)
            groupInfo = cardInfoLines(slotChange).join("\n")
        }

        const proposalData = isGroupProposal
            ? {
                type: tabType,
                title: groupTitle,
                info: groupInfo,
                statusColors: groupAttributionProposalStatusColors,
                statusNames: groupAttributionProposalStatusNames,
            }
            : {
                type: "substitution",
                title:
                    item?.substitution?.live_session
                        ?.school_subjects_classes_string || "Remplacement",
                info: formatSubstitutionInfo(item?.substitution),
                statusColors: substitutionProposalStatusColors,
                statusNames: substitutionProposalStatusNames,
            }

        const { title, info, statusColors, statusNames } = proposalData

        // Check if substitution proposal is expired (similar to backend logic)
        const isSubstitutionExpired =
            !isGroupProposal &&
            (item?.status === "expired" ||
                moment(item?.substitution?.date)
                    .add(30, "minutes")
                    .isBefore(moment()))
        const isPending =
            item?.status === "pending" &&
            (isGroupProposal
                ? item?.group_attribution?.status !== "closed"
                : !isSubstitutionExpired)

        const handleAccept = () => {
            const getStartDateInfo = () => {
                if (isGroupProposal) {
                    // For temporary absences, don't show "Première séance"
                    if (isTemporaryAbsence) {
                        return ""
                    }

                    if (!item?.group_attribution?.start_date) return ""
                    const date = moment(item.group_attribution.start_date)
                    return `Première séance : ${capitalize(
                        date.format("dddd D MMMM [à] HH[h]mm")
                    )}`
                } else {
                    const date = moment(item?.substitution?.date)
                    return capitalize(date.format("dddd D MMMM [à] HH[h]mm"))
                }
            }

            const getDialogContent = () => {
                if (isGroupProposal) {
                    const isAdhocProposal = item?.group_attribution?.is_adhoc
                    const existingSessionsForDialog =
                        item?.group_attribution?.existing_sessions || []
                    const newSessionsForDialog =
                        item?.group_attribution?.live_sessions || []

                    const perSessionMap =
                        item?.group_attribution
                            ?.school_subjects_classes_per_session
                    const schoolSubjectsClassesStringDialog =
                        item?.group_attribution?.school_subjects_classes_string

                    let studentInfo = schoolSubjectsClassesStringDialog
                    let message = ""

                    if (isAdhocProposal) {
                        // Adhoc-specific dialog content (without showing names)
                        // Extract just class and subject from school_subjects_classes_string
                        // Format from backend: "2 élèves - 5ème - Maths" -> we want "5ème - Maths"
                        const fullStringDialog =
                            schoolSubjectsClassesStringDialog || ""
                        const partsDialog = fullStringDialog.split(" - ")
                        // Remove the first part (student count) and join the rest
                        const classAndSubjectDialog =
                            partsDialog.length > 1
                                ? partsDialog.slice(1).join(" - ")
                                : ""

                        // Build title without student names
                        if (classAndSubjectDialog) {
                            studentInfo = `Nouvel élève ${classAndSubjectDialog}`
                        } else {
                            studentInfo = `Nouvel élève`
                        }

                        const startDateInfo = getStartDateInfo()
                        const sections = [studentInfo]

                        // Show existing sessions first with calendar emoji
                        if (existingSessionsForDialog.length > 0) {
                            sections.push(
                                formatTimeSlots(
                                    existingSessionsForDialog,
                                    "📅 Créneaux existants où l'élève sera ajouté"
                                )
                            )
                        }

                        // Then show new sessions with + emoji
                        if (newSessionsForDialog.length > 0) {
                            sections.push(
                                formatTimeSlots(
                                    newSessionsForDialog,
                                    "➕ Nouveaux créneaux à créer"
                                )
                            )
                        }

                        if (startDateInfo) {
                            sections.push(startDateInfo)
                        }

                        message = "\n" + sections.filter(Boolean).join("\n")

                        // Add adhoc-specific message
                        message +=
                            "\n\nℹ️ Si tu ne l'acceptes pas, l'élève ne sera pas ajouté à tes séances existantes et aucun groupe ne sera créé."
                    } else {
                        // Regular proposal dialog content
                        let sessionInfo = ""
                        const perSessionStringsDialog = buildPerSessionDisplayStrings(
                            newSessionsForDialog,
                            perSessionMap
                        )
                        if (
                            perSessionStringsDialog.length > 0 &&
                            !isTemporaryAbsence
                        ) {
                            const studentMatch =
                                schoolSubjectsClassesStringDialog?.match(
                                    /^(\d+\s+élèves?)/
                                )
                            const studentCount = studentMatch ? studentMatch[1] : ""

                            const sessionCount = perSessionStringsDialog.length
                            const sessionWord = sessionCount > 1 ? 'séances' : 'séance'
                            studentInfo = studentCount ? `${studentCount} - ${sessionCount} ${sessionWord}` : studentInfo

                            sessionInfo = perSessionStringsDialog.join("\n")
                        } else {
                            sessionInfo = formatTimeSlots(newSessionsForDialog)
                        }

                        const startDateInfo = getStartDateInfo()
                        message =
                            "\n" +
                            [studentInfo, sessionInfo, startDateInfo]
                                .filter(Boolean)
                                .join("\n\n")

                        // Add comment only for regular proposals
                        const comment = item?.group_attribution?.comment
                            ? `\nCommentaire : ${item.group_attribution.comment}`
                            : ""
                        message += comment
                    }

                    return message
                } else {
                    // Format exactly like SubstitutionProposalsCard
                    const substitution = item?.substitution
                    const liveSession = substitution?.live_session

                    return (
                        capitalize(
                            moment(substitution?.date)?.format(
                                "dddd" + " DD MMMM [à] HH:mm"
                            ) +
                            " - " +
                            formatMinutes(liveSession?.duration)
                        ) +
                        "\n" +
                        liveSession?.school_subjects_classes_string
                    )
                }
            }

            const getTitle = () => {
                if (isGroupProposal) {
                    if (tabType === "trial") {
                        return "Nouvelle séance d'essai"
                    }
                    return item?.group_attribution?.is_adhoc
                        ? "Nouvelle proposition pour toi"
                        : "Nouvelle proposition de groupe"
                } else {
                    return "Nouvelle proposition de remplacement"
                }
            }

            const title = getTitle()
            const content = getDialogContent()

            DialogM.show({
                champion: "zoe",
                text1: title,
                text2: content,
                buttonText3: "Masquer la proposition",
                buttonColor3: theme.colors.error,
                onPress3: () => {
                    setLoadingItems((prev) => new Set([...prev, item.id]))
                    props.apiRequest(
                        isGroupProposal
                            ? groupAttributionProposalsService.updateGroupAttributionProposalStatus
                            : substitutionProposalsService.updateSubstitutionProposalStatus,
                        {
                            id: item.id,
                        },
                        {
                            status: "rejected",
                        }
                    )
                },
                buttonText2: "Retour",
                buttonText1: "Accepter la proposition",
                buttonColor1: theme.colors.success,
                onPress: () => {
                    setTimeout(() => {
                        // Build confirmation text based on proposal type and scenario
                        const confirmationText = (() => {
                            if (!isGroupProposal) {
                                return "Es-tu sûr.e d'accepter ce remplacement ?"
                            }

                            // Check if it's a trial session
                            const firstLiveSession =
                                item?.group_attribution?.live_sessions?.[0]
                            const isTrial =
                                firstLiveSession?.live_room?.type === "trial"

                            if (isTrial) {
                                return "Es-tu sûr.e de candidater à cette séance d'essai ?"
                            }

                            // For adhoc proposals, distinguish the scenarios
                            const isAdhocProposal =
                                item?.group_attribution?.is_adhoc
                            if (isAdhocProposal) {
                                const existingSessionsForDialog =
                                    item?.group_attribution
                                        ?.existing_sessions || []
                                const newSessionsForDialog =
                                    item?.group_attribution?.live_sessions || []
                                const hasNew = newSessionsForDialog.length > 0
                                const hasExisting =
                                    existingSessionsForDialog.length > 0

                                if (hasNew && hasExisting) {
                                    return "Es-tu sûr.e de candidater ? Tu créeras un nouveau live et accueilleras l'élève dans tes séances existantes."
                                } else if (hasExisting) {
                                    return "Es-tu sûr.e d'accueillir cet élève dans tes séances existantes ?"
                                }
                                // new_only or other cases fall back to default
                            }

                            return "Es-tu sûr.e de candidater à cette proposition ?"
                        })()

                        const confirmationMessage = isGroupProposal
                            ? `${content}\n\nEn candidatant, tu confirmes être prêt.e à assurer ces séances.\n\nTu recevras une confirmation si c'est validé pour toi.`
                            : content

                        const confirmButtonText = isGroupProposal
                            ? "Je candidate"
                            : "Je confirme que j'accepte"

                        DialogM.show({
                            text1: confirmationText,
                            text2: confirmationMessage,
                            buttonText1: confirmButtonText,
                            onPress: () => {
                                setLoadingItems(
                                    (prev) => new Set([...prev, item.id])
                                )
                                props.apiRequest(
                                    isGroupProposal
                                        ? groupAttributionProposalsService.updateGroupAttributionProposalStatus
                                        : substitutionProposalsService.updateSubstitutionProposalStatus,
                                    {
                                        id: item.id,
                                    },
                                    {
                                        status: "accepted",
                                    }
                                )
                            },
                            buttonText2: "Annuler",
                        })
                    }, 200)
                },
                exitButton: true,
            })
        }

        const handleSlotChange = () => {
            const pick = Number(slotChange?.pick) || 0
            const answer = (body) => {
                setLoadingItems((prev) => new Set([...prev, item.id]))
                props.apiRequest(
                    groupAttributionProposalsService.updateGroupAttributionProposalStatus,
                    { id: item.id },
                    body
                )
            }
            // DialogM takes its buttons' state at show time: re-showing it on each tap
            // keeps the accept button enabled only with exactly `pick` créneaux chosen.
            const show = (selected) =>
                DialogM.show({
                    champion: "zoe",
                    text1: dialogTitle(slotChange),
                    component: (
                        <SlotChangeDialogContent
                            offer={slotChange}
                            selected={selected}
                            onToggle={(liveSessionId) =>
                                show(toggleSlot(selected, liveSessionId, pick))
                            }
                        />
                    ),
                    buttonText1: acceptButtonLabel(pick),
                    buttonColor1: theme.colors.success,
                    buttonDisabled1: !canAccept(selected, pick),
                    onPress: () => answer(buildAcceptBody(selected)),
                    buttonText3: DECLINE_BUTTON_LABEL,
                    buttonColor3: theme.colors.error,
                    // DialogM hides itself after its button's callback: the next dialog opens once it has, like the one above.
                    onPress3: () => setTimeout(() => confirmDecline(selected), 200),
                    onPressBack: () => {},
                    exitButton: true,
                })
            // « Je ne peux pas » déclenche le repli de la famille : il le lit avant de répondre.
            const confirmDecline = (selected) =>
                DialogM.show({
                    champion: "zoe",
                    text1: DECLINE_CONFIRM_TITLE,
                    text2: declineConsequence(slotChange),
                    buttonText1: DECLINE_BUTTON_LABEL,
                    buttonColor1: theme.colors.error,
                    onPress: () => answer(buildDeclineBody()),
                    buttonText3: DECLINE_BACK_LABEL,
                    onPress3: () => setTimeout(() => show(selected), 200),
                    onPressBack: () => {},
                    exitButton: true,
                })
            show(initialSelection(slotChange))
        }

        const handleWithdraw = () => {
            DialogM.show({
                text1: "Retirer ta candidature ?",
                text2: "Tu ne seras plus candidat.e pour cette proposition.",
                buttonText1: "Retirer ma candidature",
                buttonColor1: theme.colors.error,
                onPress: () => {
                    setLoadingItems((prev) => new Set([...prev, item.id]))
                    props.apiRequest(
                        isGroupProposal
                            ? groupAttributionProposalsService.updateGroupAttributionProposalStatus
                            : substitutionProposalsService.updateSubstitutionProposalStatus,
                        {
                            id: item.id,
                        },
                        {
                            status: "rejected",
                        }
                    )
                },
                buttonText2: "Annuler",
            })
        }

        const handleItemPress = () => {
            if (isPending && slotChange) {
                handleSlotChange()
            } else if (isPending) {
                handleAccept()
            } else if (item?.status === "accepted") {
                handleWithdraw()
            }
        }

        const renderActionButtons = () => {
            const isItemLoading = loadingItems.has(item.id)

            return isItemLoading ? (
                <View style={{ alignItems: "center", padding: 12 }}>
                    <ActivityIndicator size="small" color={theme.colors.grey} />
                </View>
            ) : null
        }

        const renderStudentBadge = (label) => (
            <BadgeM
                style={{
                    backgroundColor: theme.colors.primary + "14",
                    color: theme.colors.primary,
                    marginVertical: 2,
                    marginRight: 2,
                    borderRadius: 10,
                    borderWidth: 1,
                    borderColor: theme.colors.primary + "2E",
                    paddingHorizontal: 8,
                }}
                textStyle={{
                    fontSize: 10,
                }}
                count={label}
                size={20}
            />
        )

        const renderStatusBadge = () => {
            const getStatusDate = () => {
                if (item?.status === "accepted" && item?.updated_at) {
                    return moment(item.updated_at).format("DD/MM [à] HH:mm")
                }
                return ""
            }

            // const finalStatus = !isPending ? "expired" : item?.status
            const finalStatus =
                (item?.status === "pending" &&
                    item?.group_attribution?.status === "closed" &&
                    isGroupProposal) ||
                isSubstitutionExpired
                    ? "expired"
                    : item?.status

            const statusPrefix = statusNames[finalStatus]?.toUpperCase() ?? ""

            const statusText = `${statusPrefix}${
                item?.status === "accepted" ? ` • ${getStatusDate()}` : ""
            }`

            return (
                <BadgeM
                    style={{
                        backgroundColor: statusColors[item?.status] + "18",
                        color: statusColors[item?.status ?? "default"],
                        marginVertical: 2,
                        marginRight: 2,
                        borderRadius: 10,
                        borderWidth: 1,
                        borderColor: statusColors[item?.status] + "30",
                        minWidth: 28,
                        paddingHorizontal: 6,
                    }}
                    textStyle={{
                        fontSize: 9,
                    }}
                    count={statusText}
                    size={20}
                />
            )
        }

        const isAccepted = item?.status === "accepted"
        const isTappable = isPending || isAccepted

        return (
            <TouchableOpacity
                onPress={handleItemPress}
                activeOpacity={isTappable ? 0.7 : 1}
                disabled={!isTappable}
                style={{
                    paddingHorizontal:
                        Math.max(12, Math.min(16, dimensions?.width * 0.04)) ||
                        12,
                    width: "100%",
                    alignSelf: "stretch",
                }}
            >
                <View
                    style={{
                        backgroundColor: isPending
                            ? theme.colors.white
                            : theme.colors.grey + "15",
                        marginVertical: 4,
                        borderRadius: 12,
                        padding:
                            Math.max(
                                12,
                                Math.min(16, dimensions?.width * 0.04)
                            ) || 12,
                        borderLeftWidth: isPending ? 3 : 0.5,
                        borderLeftColor: isPending
                            ? theme.colors.primaryLight
                            : theme.colors.grey + "15",
                        borderWidth: isPending ? 1 : 0.5,
                        borderColor: isPending
                            ? theme.colors.primaryLight
                            : theme.colors.grey + "15",
                        shadowColor: isPending
                            ? theme.colors.primary
                            : theme.colors.grey,
                        shadowOffset: {
                            width: 0,
                            height: isPending ? 1 : 0.5,
                        },
                        shadowOpacity: isPending ? 0.08 : 0.03,
                        shadowRadius: isPending ? 2 : 1,
                        width: "100%",
                        alignSelf: "stretch",
                    }}
                >
                    <View
                        style={{
                            flexDirection: "row",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                            flexWrap: "nowrap",
                        }}
                    >
                        <View
                            style={{
                                flex: 1,
                                marginRight: 8,
                                marginBottom: 0,
                                minWidth: 0,
                            }}
                        >
                            <TextM
                                fontSize={10}
                                color={theme.colors.light}
                                style={{ marginBottom: 2 }}
                            >
                                {moment(item?.created_at).format(
                                    "DD/MM à HH:mm"
                                )}
                            </TextM>
                            <View
                                style={{
                                    flexDirection: "row",
                                    alignItems: "flex-start",
                                    marginBottom: 4,
                                    flexWrap: "wrap",
                                }}
                            >
                                <TextM
                                    style={{
                                        fontSize: 14,
                                        color: isPending
                                            ? theme.colors.dark
                                            : theme.colors.grey,
                                        flex: 1,
                                        flexWrap: "wrap",
                                    }}
                                    fontWeight={"Bold"}
                                    numberOfLines={0}
                                    wrap
                                >
                                    {title}
                                </TextM>
                            </View>

                            <TextM
                                style={{
                                    marginBottom: 2,
                                    fontSize: 11,
                                    lineHeight: 16,
                                    flexWrap: "wrap",
                                }}
                                color={
                                    isPending
                                        ? theme.colors.grey
                                        : theme.colors.grey + "CC"
                                }
                                numberOfLines={0}
                                wrap
                            >
                                {info}
                            </TextM>
                        </View>

                        <View
                            style={{
                                alignItems: "flex-end",
                                justifyContent: "center",
                                minWidth: 70,
                                position: "absolute",
                                right: -7,
                                top: -7,
                            }}
                        >
                            {isPending ? (
                                <View
                                    style={{
                                        alignItems: "flex-end",
                                    }}
                                >
                                    {slotChange
                                        ? renderStudentBadge("Ton élève")
                                        : isTargeted &&
                                          renderStudentBadge(
                                              "Proposition avec ancien élève"
                                          )}
                                    {renderActionButtons()}
                                </View>
                            ) : (
                                <View
                                    style={{
                                        alignItems: "flex-end",
                                    }}
                                >
                                    {renderStatusBadge()}
                                </View>
                            )}
                        </View>

                        {isTemporaryAbsence && (
                            <View
                                style={{
                                    alignItems: "flex-end",
                                    justifyContent: "center",
                                    minWidth: 70,
                                    position: "absolute",
                                    right: -7,
                                    bottom: -7,
                                }}
                            >
                                <BadgeM
                                    style={{
                                        backgroundColor:
                                            theme.colors.warning + "18",
                                        color: theme.colors.warning,
                                        marginVertical: 2,
                                        marginRight: 2,
                                        borderRadius: 10,
                                        borderWidth: 1,
                                        borderColor:
                                            theme.colors.warning + "30",
                                        minWidth: 28,
                                        paddingHorizontal: 6,
                                    }}
                                    textStyle={{
                                        fontSize: 9,
                                    }}
                                    count="TEMPORAIRE"
                                    size={20}
                                />
                            </View>
                        )}
                    </View>
                </View>
            </TouchableOpacity>
        )
    }

    return (
        <View
            style={{
                flex: 1,
                backgroundColor: theme.colors.whiteAlmost,
                width: dimensions?.width,
                alignSelf: "center",
                paddingTop:
                    Math.max(16, Math.min(24, dimensions?.width * 0.06)) || 24,
            }}
        >
            {
                <>
                    <View
                        style={{
                            backgroundColor: theme.colors.whiteAlmost,
                            borderBottomColor: theme.colors.lightMore,
                            paddingHorizontal: 16,
                        }}
                    >
                        <View
                            style={{
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "space-between",
                            }}
                        >
                            <TitleM
                                fontWeight="ExtraBold"
                                style={{ marginBottom: 6, flex: 1 }}
                            >
                                OFFRES DE COURS
                            </TitleM>

                            <IconButtonM
                                onPress={refreshAll}
                                loading={
                                    props.getGroupAttributionProposalsRequest ||
                                    props.getSubstitutionProposalsRequest
                                }
                                iconColor={theme.colors.primary}
                                containerColor={theme.colors.white}
                                disabled={
                                    props.getGroupAttributionProposalsRequest ||
                                    props.getSubstitutionProposalsRequest
                                }
                                icon={"sync-alt"}
                                size={20}
                            />
                        </View>

                        {props.isDispoStale && (
                            <TouchableOpacity
                                onPress={() =>
                                    navigation.push("tutor/ma-dispo")
                                }
                                style={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                    backgroundColor: theme.colors.warningLight,
                                    borderWidth: 1,
                                    borderColor: "#ffe2c2",
                                    borderRadius: 12,
                                    padding: 12,
                                    marginTop: 6,
                                    marginBottom: 6,
                                }}
                            >
                                <FontAwesome5
                                    name="clock"
                                    size={18}
                                    color={theme.colors.warning}
                                    style={{ marginRight: 10 }}
                                    solid
                                />
                                <View style={{ flex: 1 }}>
                                    <TextM
                                        fontWeight="Bold"
                                        fontSize={13}
                                        wrap
                                    >
                                        Confirme tes créneaux
                                    </TextM>
                                    <TextM
                                        fontSize={12}
                                        color={theme.colors.grey}
                                        wrap
                                    >
                                        Une fois par semaine, pour continuer à
                                        recevoir des propositions.
                                    </TextM>
                                </View>
                                <FontAwesome5
                                    name="chevron-right"
                                    size={14}
                                    color={theme.colors.warning}
                                    style={{ marginLeft: 8 }}
                                />
                            </TouchableOpacity>
                        )}
                    </View>

                    {availableTabs.length > 0 ? (
                        <View
                            style={{
                                backgroundColor: theme.colors.white,
                                marginTop: 12,
                                borderRadius: 0,
                                shadowColor: theme.colors.primary,
                                shadowOffset: { width: 0, height: 4 },
                                shadowOpacity: 0.02,
                                shadowRadius: 12,
                                overflow: "hidden",
                                borderWidth: 1,
                                borderColor: theme.colors.primary + "08",
                                flex: 1,
                            }}
                        >
                            {/*
                                CONTROLLED TABS:
                                - index={activeTab}: Makes TabsProvider controlled (synced with state)
                                - onChangeIndex: Updates activeTab when user clicks a different tab
                                - This ensures activeTab state always reflects the visible tab
                            */}
                            <TabsProvider
                                index={activeTab}
                                onChangeIndex={(newIndex) =>
                                    setActiveTab(newIndex)
                                }
                            >
                                <Tabs
                                    style={{
                                        backgroundColor: theme.colors.white,
                                        width: "100%",
                                        alignSelf: "stretch",
                                        borderBottomWidth: 1,
                                        borderBottomColor:
                                        theme.colors.lightMore,
                                        shadowColor: theme.colors.grey,
                                        shadowOffset: { width: 0, height: 1 },
                                        shadowOpacity: 0.04,
                                        shadowRadius: 2,
                                        elevation: 1,
                                    }}
                                    disableSwipe={true}
                                    mode="fixed"
                                    uppercase={false}
                                    showTextLabel={true}
                                    showLeadingSpace={false}
                                    textLabelStyle={{
                                        fontSize: 11,
                                        fontWeight: "600",
                                        letterSpacing: 0,
                                        color: theme.colors.grey,
                                    }}
                                    indicatorStyle={{
                                        backgroundColor: theme.colors.primary,
                                        height: 2.5,
                                        marginHorizontal: 100,
                                    }}
                                    tabStyle={{
                                        backgroundColor: theme.colors.white,
                                        paddingVertical: 12,
                                        paddingHorizontal: 8,
                                        borderRadius: 0,
                                        flex: 1,
                                        alignItems: "center",
                                        justifyContent: "center",
                                        minWidth: 0,
                                        marginHorizontal: 0,
                                        width: "100%",
                                    }}
                                    activeTabStyle={{
                                        backgroundColor:
                                        theme.colors.primaryLighter,
                                    }}
                                    contentStyle={{
                                        backgroundColor:
                                        theme.colors.whiteAlmost,
                                        paddingTop: 20,
                                        flex: 1,
                                    }}
                                >
                                    {/*
                                        GROUPS TAB - Conditional Rendering
                                        ===================================
                                        CRITICAL FIX: Only render content when this tab is active!

                                        Before fix:
                                        - {renderTabContent()} was called for ALL tabs simultaneously
                                        - All tabs would show the same content (based on currentTabType)
                                        - Clicking "Essais" would show trial data in Groups tab → confusion

                                        After fix:
                                        - {currentTabType === "group" ? renderTabContent() : <View />}
                                        - Only renders when this IS the active tab
                                        - Inactive tabs show empty view (no API calls, no wrong data)
                                    */}
                                    {canShowGroupsTab && (
                                        <TabScreen
                                            label={`Groupes (${
                                                props
                                                    .groupAttributionProposalsMeta
                                                    ?.pending_count || 0
                                            })`}
                                            key="group"
                                        >
                                            {currentTabType === "group" ? renderTabContent() : <View style={{ flex: 1 }} />}
                                        </TabScreen>
                                    )}

                                    {/* TRIALS TAB - Same conditional rendering pattern */}
                                    {canShowTrialsTab && (
                                        <TabScreen
                                            label={`Essais (${
                                                props.trialSessionProposalMeta
                                                    ?.pending_count || 0
                                            })`}
                                            key="trial"
                                        >
                                            {currentTabType === "trial" ? renderTabContent() : <View style={{ flex: 1 }} />}
                                        </TabScreen>
                                    )}

                                    {/* REPLACEMENTS TAB - Same conditional rendering pattern */}
                                    {canShowReplacementsTab && (
                                        <TabScreen
                                            label={`${
                                                IS_WEB
                                                    ? "Remplacements"
                                                    : "Rempl."
                                            } (${
                                                props.substitutionProposalsMeta
                                                    ?.pending_count || 0
                                            })`}
                                            key="substitution"
                                        >
                                            {currentTabType === "substitution" ? renderTabContent() : <View style={{ flex: 1 }} />}
                                        </TabScreen>
                                    )}
                                </Tabs>
                            </TabsProvider>
                        </View>
                    ) : (
                        <View
                            style={{
                                backgroundColor: theme.colors.white,
                                marginTop: 12,
                                borderRadius: 0,
                                flex: 1,
                                alignItems: "center",
                                justifyContent: "center",
                                paddingVertical: 60,
                            }}
                        ></View>
                    )}
                </>
            }
        </View>
    )
}

const mapStateToProps = (state) => ({
    groupAttributionProposals:
        state.data.currentUser?.object?.group_attribution_proposals || [],
    trialAttributionProposals:
        state.data.currentUser?.object?.trial_attribution_proposals || [],
    substitutionProposals:
        state.data.currentUser?.object?.substitution_proposals || [],
    groupAttributionProposalsMeta:
        state.data.currentUser?.object?.group_attribution_proposals_meta || {},
    substitutionProposalsMeta:
        state.data.currentUser?.object?.substitution_proposals_meta || {},
    trialSessionProposalMeta:
        state.data.currentUser?.object?.trial_attribution_proposals_meta || {},
    getGroupAttributionProposalsRequest:
    state.api.getGroupAttributionProposals?.request,
    getSubstitutionProposalsRequest:
    state.api.getSubstitutionProposals?.request,
    user: state.data.currentUser?.object || {},
    // « Ma dispo » freshness — the banner below reminds the tutor to re-confirm once
    // the 7-day horizon has lapsed. Read through the shared selector so it, the
    // monthly reminder and the home task banner relancent exactly the same tutors.
    isDispoStale: availabilityNudgeFromState(state),
})

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
}

export default connect(mapStateToProps, mapDispatchToProps)(ProposalsTabScreen)
